import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@/components/AuthProvider";
import { resolveNextPath } from "@/lib/auth";

export default function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isInitializing, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const nextCandidate = searchParams.get("next");
  const nextPath = resolveNextPath(nextCandidate);

  useEffect(() => {
    if (!isInitializing && isAuthenticated) {
      navigate(nextPath, { replace: true });
    }
  }, [isAuthenticated, isInitializing, navigate, nextPath]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setIsSubmitting(true);

    try {
      await login({ email, password });
      navigate(nextPath, { replace: true });
    } catch (_error) {
      setErrorMessage("Authentication failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-zinc-600 border-t-zinc-100 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-zinc-900/80 border border-zinc-800 rounded-2xl p-8 shadow-2xl">
        <div className="mb-6">
          <p className="text-amber-500 font-semibold tracking-[0.3em] text-xs uppercase">
            KCG
          </p>
          <h1 className="text-3xl font-semibold mt-3">Dashboard Login</h1>
          <p className="text-zinc-400 text-sm mt-2">
            Sign in to access the Kitco Chart Generator dashboard.
          </p>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          {nextCandidate && !errorMessage ? (
            <div className="rounded-lg border border-zinc-700 bg-zinc-950 px-4 py-3 text-sm text-zinc-300">
              Authentication required.
            </div>
          ) : null}

          <label className="block">
            <span className="text-sm text-zinc-300">Email</span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoComplete="username"
              className="mt-2 w-full bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-3 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </label>

          <label className="block">
            <span className="text-sm text-zinc-300">Password</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              autoComplete="current-password"
              className="mt-2 w-full bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-3 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </label>

          {errorMessage ? (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {errorMessage}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-lg bg-amber-500 text-zinc-900 font-semibold py-3 hover:bg-amber-400 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? "Signing In..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
